/**
 * SVG Evolution Tree: horizontal layout (root on the left, Human on the right) with the
 * main lineage as a spine and dead-end branches curving above/below it.
 */
import { ALL_STAGES, TREE, parentOf, type StageId } from '@core/tree';
import { escapeHtml } from '../dom';
import { BRANCH_ICONS, ICONS } from '../icons';

export interface TreeOpts {
  found: Set<StageId>;
  endings: Set<StageId>;
  /** Lineage to highlight (animated flow on its edges). */
  highlight?: StageId[];
  /** Pulsing node (e.g. current stage). */
  current?: StageId;
  /** Show names under nodes. */
  labels?: boolean;
  /** Draw each branch's label on its edge. */
  branchLabels?: boolean;
}

const COL = 132;
const ROW = 92;
const R = 22;

/** Grid positions: x = depth, y = lane (0 = spine). */
function layout(): Record<StageId, { x: number; y: number }> {
  const pos = {} as Record<StageId, { x: number; y: number }>;
  const depth = (id: StageId): number => {
    let d = 0;
    let p = parentOf(id);
    while (p) {
      d++;
      p = parentOf(p);
    }
    return d;
  };
  // Spine = path to the final node; other children alternate below/above.
  const spine = new Set<StageId>();
  let s: StageId | undefined = ALL_STAGES.find((id) => TREE[id].kind === 'final');
  while (s) {
    spine.add(s);
    s = parentOf(s);
  }
  for (const id of ALL_STAGES) {
    const d = depth(id);
    if (spine.has(id)) {
      pos[id] = { x: d, y: 0 };
      continue;
    }
    const parent = parentOf(id)!;
    const sibs = TREE[parent].paths.map((p) => p.to).filter((c) => !spine.has(c));
    const i = sibs.indexOf(id);
    // First off-spine child goes above, the second below (and so on).
    const lane = i % 2 === 0 ? -(1 + Math.floor(i / 2)) : 1 + Math.floor(i / 2);
    pos[id] = { x: d, y: lane };
  }
  return pos;
}

export const TREE_LAYOUT = layout();

/** Icon for a node = the branch icon of the path that leads to it. */
export function nodeIcon(id: StageId): string {
  const parent = parentOf(id);
  if (!parent) return ICONS.dna;
  const path = TREE[parent].paths.find((p) => p.to === id);
  return (path && BRANCH_ICONS[path.key]) || ICONS.dna;
}

/** Position an inline icon <svg> inside the tree SVG. */
function place(icon: string, x: number, y: number, size: number): string {
  return icon.replace('<svg ', `<svg x="${x}" y="${y}" width="${size}" height="${size}" `);
}

export function buildTreeSvg(o: TreeOpts): string {
  const pos = TREE_LAYOUT;
  const xs = ALL_STAGES.map((id) => pos[id].x);
  const ys = ALL_STAGES.map((id) => pos[id].y);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const pad = 56;
  const w = (Math.max(...xs) - Math.min(...xs)) * COL + pad * 2;
  const h = (maxY - minY) * ROW + pad * 2;
  const P = (id: StageId) => ({ x: pad + pos[id].x * COL, y: pad + (pos[id].y - minY) * ROW });
  const hi = new Set(o.highlight ?? []);
  let edges = '';
  let nodes = '';
  for (const id of ALL_STAGES) {
    const parent = parentOf(id);
    if (!parent) continue;
    const a = P(parent);
    const b = P(id);
    const mx = (a.x + b.x) / 2;
    const on = o.found.has(id) && o.found.has(parent);
    const flow = hi.has(id) && hi.has(parent);
    const c = TREE[id].color;
    edges += `<path class="edge${on ? ' on' : ''}${flow ? ' flow' : ''}" style="--c:${c}" d="M${a.x} ${a.y} C${mx} ${a.y} ${mx} ${b.y} ${b.x} ${b.y}"/>`;
    if (o.branchLabels) {
      const path = TREE[parent].paths.find((q) => q.to === id);
      // Spine edges: centred above the line. Curved edges: left of the curve's midpoint (clear of node names).
      const curved = a.y !== b.y;
      const lx = curved ? mx - 7 : mx;
      const ly = curved ? (a.y + b.y) / 2 + 3 : a.y - 7;
      if (path) edges += `<text class="blabel${curved ? ' end' : ''}" x="${lx}" y="${ly}" style="fill:${path.color}">${escapeHtml(path.label)}</text>`;
    }
  }
  for (const id of ALL_STAGES) {
    const n = TREE[id];
    const p = P(id);
    const found = o.found.has(id);
    const isEnd = n.kind !== 'stage';
    const cls = `node codex-card${found ? ' found' : ''}${id === o.current ? ' cur' : ''}${hi.has(id) ? ' hl' : ''}`;
    const glyph = found
      ? `<g class="glyph" style="color:${n.color}">${place(nodeIcon(id), p.x - 11, p.y - 11, 22)}</g>`
      : `<text class="q" x="${p.x}" y="${p.y + 6}">?</text>`;
    const star = isEnd
      ? `<g class="${o.endings.has(id) ? 'star' : 'star dim'}">${place(ICONS.star, p.x + 9, p.y - 31, 16)}</g>`
      : '';
    const label = o.labels !== false ? `<text x="${p.x}" y="${p.y + R + 16}">${found ? escapeHtml(n.name) : '???'}${found && o.endings.has(id) ? '<tspan class="sr"> ★</tspan>' : ''}</text>` : '';
    nodes += `<g class="${cls}" data-id="${id}" data-nav style="--c:${n.color}" tabindex="-1">
      <circle class="halo" cx="${p.x}" cy="${p.y}" r="${R + 12}"/>
      <circle class="pulse" cx="${p.x}" cy="${p.y}" r="${R}" style="transform-origin:${p.x}px ${p.y}px"/>
      <circle class="ring" cx="${p.x}" cy="${p.y}" r="${isEnd ? R - 2 : R}"/>
      ${glyph}${star}${label}</g>`;
  }
  return `<svg class="tree" viewBox="0 0 ${w} ${h}" preserveAspectRatio="xMidYMid meet">${edges}${nodes}</svg>`;
}
